import { useEffect, useMemo, useRef, useState } from 'react'
import Big from 'big.js'

import csrfHeaders from 'src/utils/csrfHeaders'
import blackbaudAppearance from 'src/utils/blackbaudAppearance'
import recurrenceDescription from './recurrenceDescription'
import { genericError } from './useRecord'
import useRecaptcha from './useRecaptcha'


const scriptPromises = {}

const loadScript = url => {
  if (window.BlackbaudCheckout) return Promise.resolve()
  if (scriptPromises[url]) return scriptPromises[url]

  scriptPromises[url] = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${url}"]`)
    const script = existing || document.createElement('script')

    const onError = error => {
      // Don't cache a failed load (network blip, blocked request, CSP race) and
      // drop the dead tag, so a later mount can retry from scratch instead of
      // being permanently blocked until a full page reload.
      delete scriptPromises[url]
      script.remove()
      reject(error)
    }

    script.addEventListener('load', resolve, { once: true })
    script.addEventListener('error', onError, { once: true })

    if (!existing) {
      script.src = url
      script.async = true
      document.head.appendChild(script)
    }
  })

  return scriptPromises[url]
}

const amountToCents = ({ amount, currency }) =>
  parseInt(Big(amount || 0).times(currency?.subunit_to_unit || 1).round(0).toString())

const subscription = (observable, handler) =>
  observable?.subscribe ? observable.subscribe(handler) : null

const useBlackbaudCheckout = ({
  i18n, donation, record, failed, finished,
  blackbaudCheckoutProps, validate, enabled, showInputLabels, collectsDonor,
}) => {
  const ids = useMemo(() => {
    const suffix = Math.random().toString(36).slice(2)
    return {
      inlineContainerId: `blackbaud-checkout-inline-${suffix}`,
      buttonContainerId: `blackbaud-checkout-button-container-${suffix}`,
      buttonId: `blackbaud-checkout-button-${suffix}`,
    }
  }, [])

  // recurringEnabled is the server-side gate, so a stale cadence in client
  // state can't turn isRecurring on by itself.
  const recurring = !!blackbaudCheckoutProps?.recurringEnabled &&
    donation.state.subscription !== 'one_time'

  const checkoutRef = useRef(null)
  const transactionRef = useRef(null)
  const paymentModeRef = useRef(null)
  const completingRef = useRef(false)
  const buttonLockedRef = useRef(false)
  const validateRef = useRef(null)
  const latestRef = useRef({})
  const [ ready, setReady ] = useState(false)
  const [ buttonLocked, setButtonLocked ] = useState(false)
  const [ amountDetails, setAmountDetails ] = useState(null)

  validateRef.current = validate

  const setCheckoutButtonEnabled = enabled => {
    const button = checkoutRef.current?.completeCheckoutButtonComponent
    if (button) button.enabled = !!enabled
  }

  const visuallyLockCheckoutButton = () => {
    buttonLockedRef.current = true
    setButtonLocked(true)
  }

  const lockCheckoutButton = () => {
    visuallyLockCheckoutButton()
    setCheckoutButtonEnabled(false)
  }

  const resetCheckoutButton = () => {
    completingRef.current = false
    buttonLockedRef.current = false
    setButtonLocked(false)
    setCheckoutButtonEnabled(true)
  }

  // Blackbaud owns this button's click and authorizes the card the instant it
  // fires. Validate our form first: on failure, surface the errors and block the
  // authorization, leaving the button enabled so the donor can fix and retry.
  // We deliberately DON'T lock on a valid click — validateRef only sees our
  // fields, not Blackbaud's card, and Blackbaud silently rejects an invalid card
  // with no callback, so an optimistic lock would strand the button (the bug this
  // fixes). Blackbaud guards its own in-flight authorization; once it succeeds,
  // handleCheckoutComplete locks us through the capture step.
  const handleCheckoutButtonClick = event => {
    if (buttonLockedRef.current || completingRef.current) {
      event.preventDefault()
      event.stopImmediatePropagation()
      return
    }
    if (!validateRef.current?.()) {
      event.preventDefault()
      event.stopImmediatePropagation()
    }
  }

  const syncCheckout = () => {
    const checkout = checkoutRef.current
    if (!checkout) return

    checkout.baseAmount = amountToCents({
      amount: donation.total,
      currency: donation.currency,
    })
    // Runtime parameters, not construction config: a cadence change is a sync,
    // not a rebuild. Cleared on one-time so no stale mandate is left behind.
    checkout.isRecurring = recurring
    checkout.recurrenceDescription = recurring
      ? recurrenceDescription({ i18n, subscription: donation.state.subscription })
      : ''

    checkout.billingDetails = {
      // Blackbaud's schema is a single `name`; firstName / lastName aren't keys
      // it reads, so they never prefilled anything.
      name: [ donation.state.firstName, donation.state.lastName ]
        .filter(Boolean).join(' '),
      email: donation.state.email,
      phone: donation.state.phone,
    }
  }

  // Blackbaud's button authorizes the moment it's clicked, so the draft Order
  // can't exist beforehand. Once we hold an authorized transaction, create the
  // draft and capture against it.
  const finalize = async captcha_token => {
    const {
      blackbaudCheckoutProps, donation, failed, finished, i18n, record, recaptcha,
      collectsDonor
    } = latestRef.current
    const transaction = transactionRef.current
    if (!transaction) return

    // Clearing these lets the donor click Blackbaud's button again; each click
    // is a fresh authorization, so the single-use captcha token is reset too.
    const reset = () => {
      resetCheckoutButton()
      transactionRef.current = null
      recaptcha.reset?.()
    }

    try {
      const { errors: draftErrors, token, serializedDonation } =
        await record.saveDraft({
          order: {
            // payment_method_used is set server-side at finalize from the
            // captured transaction's actual method (see BlackbaudFinalization).
            payment_source: 'blackbaud_checkout',
            // Name / email aren't ours to send when Blackbaud is collecting
            // them; the server parks the draft on a placeholder donor instead.
            blackbaud_collects_donor: !!collectsDonor,
          }
        })

      if (draftErrors) {
        reset()
        return failed(draftErrors)
      }

      const response = await fetch(blackbaudCheckoutProps.blackbaudCheckoutsPath, {
        method: 'POST',
        headers: csrfHeaders(),
        body: JSON.stringify({
          order_id: token,
          transaction_id: transaction.id,
          authorized_amount_cents: transaction.amountDetails?.total,
          amount_details: transaction.amountDetails,
          captcha_token,
          // No account token: it exists only on the transaction, server-side.
          frequency: donation.state.subscription,
          last4: transaction.last4,
          payment_mode: transaction.paymentMode,
          donor_name: transaction.donorName,
          locale: i18n.locale,
        })
      })

      if (response.ok) {
        transactionRef.current = null
        finished({ token, serializedDonation })
      } else if (response.status >= 500) {
        // The card may already be captured server-side. Do NOT re-arm the
        // button (a fresh click would authorize a second charge) — keep it
        // locked while the backend reconciles the existing transaction.
        const json = await response.json().catch(() => ({}))
        transactionRef.current = null
        lockCheckoutButton()
        failed({ base: [ json.message || i18n.t('errors.donation_error') ] })
      } else {
        const json = await response.json().catch(() => ({}))
        reset()
        failed({ base: [ json.message || i18n.t('errors.donation_error') ] })
      }
    } catch (error) {
      console.error('Error capturing Blackbaud Checkout transaction:', error)
      reset()
      failed(genericError(i18n))
    }
  }

  // reCAPTCHA runs AFTER Blackbaud authorizes the card. If the donor abandons
  // or the challenge errors/expires, no token arrives and finalize never runs;
  // re-arm the button and clear the spinner so they aren't stranded (the
  // uncaptured authorization simply expires — no money moves).
  const handleRecaptchaUnavailable = () => {
    transactionRef.current = null
    resetCheckoutButton()
    record.setProcessing(false)
  }

  const recaptcha = useRecaptcha({
    enabled: !!blackbaudCheckoutProps?.recaptcha_enabled,
    siteKey: blackbaudCheckoutProps?.recaptchaSiteKey,
    success: finalize,
    onUnavailable: handleRecaptchaUnavailable,
  })

  latestRef.current = {
    blackbaudCheckoutProps, donation, failed, finished, i18n, record, recaptcha,
    finalize, collectsDonor
  }

  const handleCheckoutComplete = evt => {
    if (completingRef.current) return

    const { failed, i18n, record, recaptcha, finalize } = latestRef.current

    // Blackbaud reports the outcome of its own payment attempt; only record the
    // donation when it actually succeeded.
    if (evt?.success === false) {
      resetCheckoutButton()
      return failed({ base: [ evt.error?.text || i18n.t('errors.donation_error') ] })
    }

    const transaction = evt?.transaction || {}

    // Blackbaud Checkout charges the card itself and returns the transaction id;
    // our backend reads it back and records the donation.
    transactionRef.current = {
      id: transaction.id || evt?.transactionId,
      amountDetails: transaction.amountDetails || evt?.amountDetails,
      last4: transaction.card?.last4 || evt?.cardLast4,
      paymentMode: paymentModeRef.current,
      // Direct debit transactions hang no name off the REST resource, so this
      // is the server's only source for them. Beta-gated, same flag as the
      // address fields.
      donorName: transaction.address?.name,
    }

    completingRef.current = true
    lockCheckoutButton()
    record.setProcessing(true)

    if (recaptcha.available)
      recaptcha.execute()
    else
      finalize()
  }

  useEffect(() => {
    if (!enabled || !blackbaudCheckoutProps) return

    let cancelled = false
    let checkout
    let subscriptions = []

    loadScript(blackbaudCheckoutProps.scriptUrl)
      .then(() => {
        if (cancelled) return

        checkout = window.BlackbaudCheckout({
          workflowMode: 'inline',
          paymentConfigurationId: blackbaudCheckoutProps.paymentConfigurationId,
          applicationId: blackbaudCheckoutProps.applicationId,
          ...(blackbaudCheckoutProps.applicationName
            ? { applicationName: blackbaudCheckoutProps.applicationName } : {}),
          // Apple Pay / Google Pay tokens schedule as card gifts; Amazon Pay's
          // aren't confirmed to, so it's off for recurring.
          paymentMethodOptions: recurring
            ? {
              ...blackbaudCheckoutProps.paymentMethodOptions,
              wallets: {
                ...blackbaudCheckoutProps.paymentMethodOptions.wallets,
                amazonPayEnabled: false,
              },
            }
            : blackbaudCheckoutProps.paymentMethodOptions,
          // Suppressed for recurring: the offset applies to this transaction
          // only, so later installments would charge a different amount.
          feeOffsetType: recurring ? 0 : blackbaudCheckoutProps.feeOffsetType,
          currency: donation.currency.iso_code,
          // Let Blackbaud's iframe collect the billing address rather than us
          // rendering our own fields; it comes back on the transaction as
          // billing_info (see Widgets::BlackbaudFinalization). The boolean does
          // nothing without the matching betas entry.
          ...(donation.collectAddress && {
            requireBillingAddress: true,
            betas: [ 'require-address' ],
          }),
          ...blackbaudAppearance({ showInputLabels }),
        })
        checkoutRef.current = checkout

        subscriptions = [
          subscription(checkout.inlineCheckoutComponent.ready, value => setReady(!!value)),
          subscription(checkout.checkoutReady, () => setReady(true)),
          subscription(checkout.checkoutComplete, handleCheckoutComplete),
          subscription(checkout.amountDetailsChanges, setAmountDetails),
          // The only report of which method the donor picked: their transactions
          // carry no method field, so the server can't derive it after the fact.
          subscription(checkout.paymentModeChanges, mode => {
            paymentModeRef.current = mode
          }),
        ].filter(Boolean)

        checkout.inlineCheckoutComponent.mount(ids.inlineContainerId)
        checkout.completeCheckoutButtonComponent.mount(ids.buttonContainerId, ids.buttonId)
        const button = document.getElementById(ids.buttonId)
        button?.addEventListener('click', handleCheckoutButtonClick, true)
        syncCheckout()
      })
      .catch(error => {
        console.error('Error loading Blackbaud Checkout:', error)
        latestRef.current.failed(genericError(latestRef.current.i18n))
      })

    return () => {
      cancelled = true
      subscriptions.forEach(sub => sub.unsubscribe?.())
      checkout?.destroy?.()
      if (checkoutRef.current === checkout) checkoutRef.current = null
      transactionRef.current = null
      paymentModeRef.current = null
      completingRef.current = false
      buttonLockedRef.current = false
      setButtonLocked(false)
      document.getElementById(ids.buttonId)
        ?.removeEventListener('click', handleCheckoutButtonClick, true)
      setReady(false)
      setAmountDetails(null)
    }
  }, [
    enabled,
    blackbaudCheckoutProps?.scriptUrl,
    blackbaudCheckoutProps?.paymentConfigurationId,
    blackbaudCheckoutProps?.feeOffsetType,
    donation.currency.iso_code,
    // requireBillingAddress is construction-time config, so a mid-session flip
    // (crossing $5k, opting into a receipt) has to rebuild the workflow.
    donation.collectAddress,
    // feeOffsetType is construction-time config. The boolean, not the cadence
    // string: monthly -> quarterly must not remount and lose card input.
    recurring,
    ids.inlineContainerId,
    ids.buttonContainerId,
    ids.buttonId,
  ])

  useEffect(() => {
    if (!enabled || !checkoutRef.current) return
    try {
      syncCheckout()
    } catch (error) {
      console.error('Error updating Blackbaud Checkout:', error)
    }
  }, [
    enabled,
    donation.total,
    donation.state.subscription,
    donation.state.firstName,
    donation.state.lastName,
    donation.state.email,
    donation.state.phone,
  ])

  // Blackbaud's button tracks its own enabled flag (defaults to disabled) and
  // silently drops the donor's click until we flip it on. Enable it once it has
  // mounted and keep it enabled — validity is enforced on click instead (see
  // handleCheckoutButtonClick), so the donor never faces a button that looks
  // live but does nothing. lockCheckoutButton/resetCheckoutButton toggle the
  // flag around an in-flight submit. (`ready` re-runs this once the component
  // has mounted.)
  useEffect(() => {
    setCheckoutButtonEnabled(!buttonLockedRef.current && !completingRef.current)
  }, [ ready ])

  // Blackbaud offers the donor an in-iframe "cover the fees" option
  // (feeOffsetType: 1) and reports the fee via amountDetails (in cents). Add it
  // on top of our live base total so the "Pay $X" button reflects what Blackbaud
  // will authorize, while the base still tracks amount edits without iframe lag.
  const feeOffsetCents = amountDetails?.donorOffsettingFees
    ? amountDetails.feeOffsetAmount
    : 0
  const total = Big(donation.total)
    .plus(Big(feeOffsetCents).div(donation.currency.subunit_to_unit || 1))
    .toFixed(donation.currency.exponent)

  return {
    ...ids,
    ready,
    buttonLocked,
    total,
    renderRecaptcha: recaptcha.renderElement,
  }
}

export default useBlackbaudCheckout
